import {
  useEffect,
  useMemo,
  useState
} from 'react'
import { useForm } from 'react-hook-form'
import {
  useDispatch,
  useSelector
} from 'react-redux'
import { Stack } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { SingleButton } from 'common/Buttons'
import {
  getEditProfileMessage,
  getNotificationMessage,
  setIsLocationOpen,
  setUpdateUserProfileStatus,
  setUserPosition,
  setUserSectionPage,
  updateUserProfile
} from 'store'
import {
  REJECTED,
  USER_NAME,
  LOCATION_TYPE,
  PENDING,
  SINGLE_PROFILE,
  SUCCEEDED
} from 'utils/constants'
import { EditProfileValidationScheme } from 'validation'
import { getAddress } from 'ethers'

import { Affinities } from 'components/UserSection/EditProfile/Affinities'
import { CodeWords } from 'components/UserSection/EditProfile/CodeWords'
import { Location } from 'components/UserSection/EditProfile/Location'
import { SocialProfiles } from 'components/UserSection/EditProfile/SocialProfiles'
import { UserInformation } from 'components/UserSection/EditProfile/UserInformation'
import { Wallet } from 'components/UserSection/EditProfile/Wallet'

import {
  getMarkers,
  setIsChangesSaved,
  setIsProfileChanged,
  setIsProfileIncomplete,
  setUserEdited,
  usersSelectors
} from '../../../store'

import { ResetProfile } from './ResetProfile'

const isValidAddress = (address) => {
  try {
    getAddress(address)

    return true
  } catch (err) {
    return false
  }
}

export const EditProfile = ({
  selectedUser,
  setSocialLinksArray,
  socialLinksArray,
  setSocialLinkSelected,
  socialLinkSelected,
  handleSelectUser
}) => {
  const {
    id,
    code_words,
    profile_picture,
    location_type,
    affinities,
    social_links,
    profile_type,
    full_name,
    personal_quote,
    banner,
    coordinates,
    wallet_address
  } = selectedUser

  const stringifiedCW = JSON.stringify(code_words)
  const stringifiedAffi = JSON.stringify(affinities)
  const stringifiedSL = JSON.stringify(social_links)
  const stringifiedPP = JSON.stringify(profile_picture)
  const stringifiedB = JSON.stringify(banner)

  const originalLocType = useMemo(() => {
    const locationType = selectedUser?.location_type
    const keys = locationType ? Object.keys(locationType) : []

    if (keys.includes(LOCATION_TYPE.LiveApproximate)) {
      return '1'
    }

    if (
      keys.includes(LOCATION_TYPE.StaticLocation)
      || (
        !full_name
        && localStorage.getItem('isLocDisabled') === 'true'
      )
    ) {
      return '2'
    }

    return '0'
  }, [selectedUser?.location_type])

  const dispatch = useDispatch()
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(EditProfileValidationScheme)
  })

  const {
    isChangesSaved,
    updateUserProfileStatus,
    userEdited,
    userPosition,
    userProfile
  } = useSelector(usersSelectors)

  const [codeWordsArray, setCodeWordsArray] = useState(code_words)
  const [affinitiesArray, setAffinitiesArray] = useState(affinities)
  const [profileInfoReady, setProfileInfoReady] = useState(false)
  const [isDeleteImage, setIsDeleteImage] = useState(false)
  const [isDeleteImage2, setIsDeleteImage2] = useState(false)
  const [incomplete, setIncomplete] = useState(false)
  const [userQuote, setUserQuote] = useState(personal_quote)
  const [nameChanges, setNameChanges] = useState(full_name)
  const [walletAddress, setWalletAddress] = useState(wallet_address)
  const [selectLocation, setSelectLocation] = useState(
    location_type ? Object.values(location_type)[0] : 0
  )
  const [profilePicture, setProfilePicture] = useState({
    image: '',
    imagePath: profile_picture
  })
  const [coverPicture, setCoverPicture] = useState({
    image: '',
    imagePath: banner
  })

  const onSubmit = (data) => {
    const isLocDisabled = localStorage.getItem('isLocDisabled')
    dispatch(getEditProfileMessage({
      message1: [],
      message2: []
    }))
    // If the user is using static location but didn't enter a location
    if (((selectLocation === 2 || selectLocation === '2') && userPosition === null) || affinitiesArray.length <= 0 || data.userName.length <= 0) {
      dispatch(
        getNotificationMessage({
          type: REJECTED,
          message: 'You are missing a location, affinity or profile name',
          isEditProfile: true
        })
      )
      setIncomplete(true)
      setTimeout(() => setIncomplete(false), 2000)
    } else if ((isLocDisabled === 'true' && (selectLocation === 0 || selectLocation === '0' || selectLocation === 1 || selectLocation === '1'))
      || 'geolocation' in navigator === false) {
      dispatch(
        getNotificationMessage({
          type: REJECTED,
          message: 'You cannot use "' + LOCATION_TYPE.LiveLocation + '" or "' + LOCATION_TYPE.LiveApproximate + '" because you have disabled your browser location permission.',
          isEditProfile: true
        })
      )
    } else if (
      walletAddress !== null
      && walletAddress !== ''
      && !isValidAddress(walletAddress)
    ) {
      dispatch(
        getNotificationMessage({
          type: REJECTED,
          message: 'Your wallet address format is not valid',
          isEditProfile: true
        })
      )
    } else {
      const userData = {
        id,
        profile_type,
        affinities: affinitiesArray.map((item) => item.id),
        code_words: codeWordsArray.map((item) => item.title),
        location_type: selectLocation,
        full_name: data.userName,
        personal_quote: userQuote,
        socialLinks: socialLinksArray,
        coordinates: userPosition,
        wallet_address: walletAddress
      }

      const formData = new FormData()
      const formData2 = new FormData()

      if (profilePicture.image && coverPicture.image) {
        formData.append('profile_picture', profilePicture.image)
        formData2.append('banner', coverPicture.image)
        dispatch(
          updateUserProfile({
            userData,
            formData,
            formData2,
            id
          })
        )
        dispatch(setUserEdited(selectedUser))
      } else if (profilePicture.image && !isDeleteImage2) {
        formData.append('profile_picture', profilePicture.image)
        dispatch(
          updateUserProfile({
            userData,
            formData,
            id
          })
        )
        dispatch(setUserEdited(selectedUser))
      } else if (!isDeleteImage && coverPicture.image) {
        formData2.append('banner', coverPicture.image)
        dispatch(
          updateUserProfile({
            userData,
            formData2,
            id
          })
        )
        dispatch(setUserEdited(selectedUser))
      } else {
        dispatch(updateUserProfile({
          userData,
          id
        }))
        dispatch(setUserEdited(selectedUser))
      }
    }
    setTimeout(() => dispatch(getMarkers({ isAuth: true })), 1000)
  }

  useEffect(() => {
    if (!profileInfoReady) {
      dispatch(setIsProfileChanged(false))
      dispatch(setIsProfileIncomplete(false))
      setSocialLinksArray(social_links)
      if (coordinates && coordinates.length) {
        dispatch(setUserPosition(coordinates))
      }
      setSocialLinkSelected([])
      dispatch(setIsLocationOpen(false))

      setProfileInfoReady(true)
    }
  }, [])

  // To check if there are changes made
  useEffect(() => {
    if (profileInfoReady) {
      let originalCoordinates = selectedUser.coordinates === undefined
        || selectedUser.coordinates === null
        ? {
          0: localStorage.getItem('lat'),
          1: localStorage.getItem('lng')
        }
        : {
          0: selectedUser.coordinates[0],
          1: selectedUser.coordinates[1]
        }

      if (
        (
          nameChanges === full_name
          || (
            nameChanges === ''
            && full_name === null
          )
        )
        && (
          userQuote === personal_quote
          || (
            userQuote === ''
            && personal_quote === null
          )
        )
        && JSON.stringify(codeWordsArray) === stringifiedCW
        && JSON.stringify(affinitiesArray) === stringifiedAffi
        && JSON.stringify(socialLinksArray) === stringifiedSL
        && JSON.stringify(profilePicture.imagePath) === stringifiedPP
        && JSON.stringify(coverPicture.imagePath) === stringifiedB
        && (
          (
            userPosition
            && +userPosition[0] === +originalCoordinates[0]
            && +userPosition[1] === +originalCoordinates[1]
            && +selectLocation === +originalLocType
          )
          || (
            coordinates === null
            && full_name
            && +selectLocation === +originalLocType
          )
        )
        && walletAddress === wallet_address
      ) {
        dispatch(setIsProfileChanged(false))
        dispatch(setIsProfileIncomplete(false))
      } else if (
        nameChanges === ''
        || !affinitiesArray.length
        || (
          userPosition === null
          && parseInt(selectLocation, 10) === 2
        )
      ) {
        dispatch(setIsProfileChanged(true))
        dispatch(setIsProfileIncomplete(true))
      } else {
        dispatch(setIsProfileChanged(true))
        dispatch(setIsProfileIncomplete(false))
      }
    }
  }, [
    nameChanges,
    userQuote,
    codeWordsArray,
    affinitiesArray,
    userPosition,
    socialLinksArray,
    profilePicture,
    coverPicture,
    selectLocation,
    walletAddress
  ])

  // To submit if save changed prompt is accepted
  useEffect(() => {
    if (isChangesSaved === true) {
      handleSubmit(onSubmit)()
        .then(
          dispatch(setIsChangesSaved(false))
        )
    }
  }, [isChangesSaved])

  useEffect(() => {
    if (updateUserProfileStatus === SUCCEEDED) {
      dispatch(setUpdateUserProfileStatus(0))

      if (userProfile) {
        dispatch(setUserSectionPage(null))
        dispatch(setUserSectionPage(SINGLE_PROFILE))

        let drawerContents = document.querySelector(`#${SINGLE_PROFILE}-drawer-body`)
        if (drawerContents) {
          drawerContents.scrollTop = 0
        }

        userProfile.map((item) => {
          if (userEdited.id === item.id) {
            handleSelectUser(item)
            dispatch(setUserEdited([]))
          }

          return null
        })
      }
    }
  }, [updateUserProfileStatus])

  return (
    <Stack
      direction="column"
      spacing="16px"
      padding="24px 65px"
      backgroundColor="white"
    >
      <UserInformation
        setIsDeleteImage={setIsDeleteImage}
        setIsDeleteImage2={setIsDeleteImage2}
        setProfilePicture={setProfilePicture}
        setCoverPicture={setCoverPicture}
        profilePicture={profilePicture}
        coverPicture={coverPicture}
        error={errors.userName}
        register={register(USER_NAME)}
        value={full_name}
        setNameChanges={setNameChanges}
        quote={userQuote}
        setQuote={setUserQuote}
        setIncomplete={setIncomplete}
      />
      <CodeWords
        codeWordsArray={codeWordsArray}
        setCodeWordsArray={setCodeWordsArray}
      />
      <Affinities
        affinitiesArray={affinitiesArray}
        setAffinitiesArray={setAffinitiesArray}
        setIncomplete={setIncomplete}
        incomplete={incomplete}
      />
      <SocialProfiles
        socialLinksArray={socialLinksArray}
        setSocialLinksArray={setSocialLinksArray}
        setSocialLinkSelected={setSocialLinkSelected}
        socialLinkSelected={socialLinkSelected}
      />
      <Stack />
      <Wallet
        walletAddress={walletAddress}
        setWalletAddress={setWalletAddress}
      />
      <Location
        setSelectLocation={setSelectLocation}
        location_type={location_type}
        incomplete={incomplete}
        full_name={full_name}
      />
      <SingleButton
        customStyled={{
          fontSize: '18px',
          color: incomplete ? 'red' : 'black'
        }}
        name="save-changes"
        click={() => {
          if (updateUserProfileStatus !== PENDING && !incomplete) {
            handleSubmit(onSubmit)()
          }
        }}
        isLoading={updateUserProfileStatus === PENDING}
        text={incomplete ? 'Incomplete' : 'Save changes'}
      />

      { full_name && (<ResetProfile id={id} />) }
    </Stack>
  )
}
